import * as React from 'react';
import { Chips } from '@cian/ui-kit';

import {
  floorTypeDescriptors,
  NOT_FIRST_VALUE,
  NOT_LAST_VALUE,
  ONLY_LAST_VALUE,
  NOT_FIRST_NOT_LAST_VALUE,
} from '../../constants/descriptors';
import { isDescriptorActive } from '../../utils/filters';
import { connectToDescriptors, IDescriptorProps } from '../сonnectToDescriptors';
import { TTypedThunkDispatch } from '../../types/thunk';
import { getOffersCount } from '../../actions/offersData';
import { updateTags } from '../../actions/filterTags';

export interface IFloorTypeProps extends IDescriptorProps<string> {
  getOffersCount(): void;
  updateTags(): void;
}

const FloorTypeComponent: React.FC<IFloorTypeProps> = React.memo(
  ({ actionCreator, getOffersCount, updateTags, jsonQuery, descriptors }) => {
    const options = React.useMemo(
      () =>
        descriptors
          .filter(({ state }) => state !== '' && state !== NOT_FIRST_NOT_LAST_VALUE)
          .map(({ state, label }) => ({
            label,
            value: state,
          })),
      [descriptors],
    );

    const activeDescriptors = descriptors.filter(descriptor => isDescriptorActive(descriptor)(jsonQuery));
    const currentValues = React.useMemo(() => {
      // Если активен дескриптор "не первый и не последний", разворачиваем его в два отдельных значения
      if (activeDescriptors.some(d => d.state === NOT_FIRST_NOT_LAST_VALUE)) {
        return [NOT_FIRST_VALUE, NOT_LAST_VALUE];
      }

      return activeDescriptors.map(d => d.state);
    }, [activeDescriptors]);

    const handleChipClick = React.useCallback(
      (value: string) => {
        const isNotFirstActive = currentValues.includes(NOT_FIRST_VALUE);
        const isNotLastActive = currentValues.includes(NOT_LAST_VALUE);
        const isOnlyLastActive = currentValues.includes(ONLY_LAST_VALUE);

        const isBothActive = isNotFirstActive && isNotLastActive;

        const toggleDescriptor = (state: string) => {
          const descriptor = descriptors.find(d => d.state === state);

          if (descriptor) {
            actionCreator(descriptor)();
          }
        };

        const clearAll = () => {
          activeDescriptors.forEach(descriptor => {
            actionCreator(descriptor)();
          });
        };

        // Обработка парных чипов (могут быть активны одновременно)
        const handlePairableChip = (currentValue: string, otherValue: string) => {
          if (isOnlyLastActive) {
            toggleDescriptor(ONLY_LAST_VALUE);
          }

          const isCurrentActive = currentValues.includes(currentValue);
          const isOtherActive = currentValues.includes(otherValue);

          if (isBothActive) {
            // Оба активны -> оставляем только другой
            toggleDescriptor(NOT_FIRST_NOT_LAST_VALUE);
            toggleDescriptor(otherValue);
          } else if (isCurrentActive) {
            // Только текущий активен -> отключаем
            toggleDescriptor(currentValue);
          } else if (isOtherActive) {
            // Другой активен -> включаем комбо
            toggleDescriptor(otherValue);
            toggleDescriptor(NOT_FIRST_NOT_LAST_VALUE);
          } else {
            // Ничего не активно -> включаем только текущий
            toggleDescriptor(currentValue);
          }
        };

        if (value === ONLY_LAST_VALUE) {
          if (!isOnlyLastActive) {
            clearAll();
          }

          toggleDescriptor(ONLY_LAST_VALUE);
        } else if (value === NOT_FIRST_VALUE) {
          handlePairableChip(NOT_FIRST_VALUE, NOT_LAST_VALUE);
        } else if (value === NOT_LAST_VALUE) {
          handlePairableChip(NOT_LAST_VALUE, NOT_FIRST_VALUE);
        }

        updateTags();
        getOffersCount();
      },
      [actionCreator, descriptors, getOffersCount, updateTags, currentValues, activeDescriptors],
    );

    return (
      <Chips options={options} size="M" value={currentValues} selectType="multiple" onChipClick={handleChipClick} />
    );
  },
);

FloorTypeComponent.displayName = 'FloorTypeComponent';

function mapDispatchToProps(dispatch: TTypedThunkDispatch) {
  return {
    getOffersCount: () => {
      dispatch(getOffersCount());
    },
    updateTags: () => {
      dispatch(updateTags());
    },
  };
}

export const FloorType = connectToDescriptors(floorTypeDescriptors, mapDispatchToProps)(FloorTypeComponent);
